import {
  BookOpen,
  CalendarDays,
  LayoutGrid,
  ShieldCheck,
  ShieldAlert,
  LogOut,
  Zap,
  Lightbulb,
  Settings,
  Shield,
  ChevronLeft,
  ChevronRight,
  Compass,
  Users,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useEffect, useState, useMemo, Suspense, useCallback } from "react";
import { lazyRetry } from "@/lib/lazyRetry";
import { supabase } from "@/integrations/supabase/client";
import { useProfile } from "@/hooks/useProfile";
import { useInterfaceMode } from "@/hooks/useInterfaceMode";
import { motion } from "framer-motion";
import ThemeToggle from "@/components/layout/ThemeToggle";

const AdminIdeaQuickInput = lazyRetry(() => import("@/components/admin/AdminIdeaQuickInput"));

import { AppliPlusSection } from "@/components/layout/AppliPlusSection";
import { SidebarGlobalSearch } from "@/components/layout/SidebarGlobalSearch";
import { SidebarShortcuts } from "@/components/layout/SidebarShortcuts";

import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarFooter,
  SidebarHeader,
  useSidebar,
} from "@/components/ui/sidebar";
import { NavLink } from "@/components/NavLink";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// ── Zone B: 6 fixed nav items ──
const NAV_ITEMS = [
  { title: "Bibliothèque", url: "/library", icon: BookOpen },
  { title: "Groupes", url: "/groups", icon: Users },
  { title: "Découvrir", url: "/features", icon: Compass },
];

// Module accent colors per route (UI-only, visual accent on active item)
const MODULE_ACCENT: Record<string, string> = {
  "/programs": "var(--module-programs)",
  "/library": "var(--module-library)",
  "/features": "var(--module-library)",
};

export function AppSidebar() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { state, toggleSidebar } = useSidebar();
  const collapsed = state === "collapsed";
  const { profile } = useProfile();
  const { mode } = useInterfaceMode();
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [ideaOpen, setIdeaOpen] = useState(false);

  const checkRoles = useCallback(() => {
    if (!user) return;
    supabase.rpc("get_user_roles", { _user_id: user.id }).then(({ data }) => {
      if (data) {
        const parsed = typeof data === "string" ? JSON.parse(data) : data;
        setIsAdmin(parsed.is_admin ?? false);
        setIsSuperAdmin(parsed.is_super_admin ?? false);
      }
    });
  }, [user]);

  useEffect(() => { checkRoles(); }, [checkRoles]);
  useEffect(() => {
    const onFocus = () => checkRoles();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [checkRoles]);

  const firstName = profile?.display_name?.split(" ")[0] ?? "";
  const initials = (profile?.display_name ?? "U").charAt(0).toUpperCase();

  return (
    <Sidebar collapsible="icon">
      {/* ═══ ZONE A — Header ═══ */}
      <SidebarHeader className={collapsed ? "flex flex-col items-center gap-2 p-2" : "p-4 space-y-3"}>
        {/* Logo row */}
        {!collapsed ? (
          <div className="flex items-center justify-between">
            <button onClick={() => navigate("/home")} className="flex items-center gap-1.5 cursor-pointer">
              <Zap className="h-4 w-4 text-perform-power" />
              <span className="font-heading text-[11px] font-bold tracking-widest text-perform-power uppercase">
                Perform
              </span>
            </button>
            <motion.button
              onClick={toggleSidebar}
              className="flex h-7 w-7 items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              aria-label="Réduire le menu"
            >
              <ChevronLeft className="h-4 w-4 text-muted-foreground" />
            </motion.button>
          </div>
        ) : (
          <motion.button
            onClick={toggleSidebar}
            className="flex h-11 w-11 items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Ouvrir le menu"
          >
            <Zap className="h-5 w-5 text-perform-power" />
          </motion.button>
        )}


        {/* Collapsed: expand chevron below logo */}
        {collapsed && (
          <motion.button
            onClick={toggleSidebar}
            className="flex h-6 w-6 items-center justify-center rounded-md hover:bg-sidebar-accent transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            aria-label="Ouvrir le menu"
          >
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
          </motion.button>
        )}
      </SidebarHeader>

      {/* ═══ ZONE B — Main navigation ═══ */}
      <SidebarContent>
        {/* Global quick-nav search (expanded only) */}
        {!collapsed && <SidebarGlobalSearch />}

        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV_ITEMS.map((item) => {
                const isActive = location.pathname === item.url;
                const accent = MODULE_ACCENT[item.url];
                const activeStyle =
                  isActive && accent
                    ? {
                        color: `hsl(${accent})`,
                        borderLeft: `2px solid hsl(${accent})`,
                        backgroundColor: `hsl(${accent} / 0.10)`,
                      }
                    : undefined;
                return (
                  <SidebarMenuItem key={item.title}>
                    <SidebarMenuButton
                      asChild
                      isActive={isActive}
                      tooltip={item.title}
                    >
                      <NavLink
                        to={item.url}
                        end
                        activeClassName="font-medium"
                        className="hover:bg-white/5 transition-colors duration-150"
                        style={activeStyle}
                      >
                        <item.icon
                          className="h-[18px] w-[18px]"
                          style={isActive && accent ? { color: `hsl(${accent})` } : undefined}
                        />
                        <span className="text-sm font-medium">{item.title}</span>
                      </NavLink>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Raccourcis — active program shortcut (expanded only) */}
        {!collapsed && <SidebarShortcuts />}
      </SidebarContent>

      {/* ═══ ZONE C — Footer ═══ */}
      <SidebarFooter className="mt-auto pb-4 px-2">
        <SidebarMenu>
          {/* Appli + section — pinned modules + catalog */}
          {!collapsed && mode === "advanced" && <AppliPlusSection />}

          {/* Modules now nested inside Réglages dropdown below */}


          {/* Idea quick input (admin only) */}
          {isAdmin && !collapsed && (
            <SidebarMenuItem>
              <SidebarMenuButton
                tooltip="Idée rapide"
                onClick={() => setIdeaOpen((v) => !v)}
                className={ideaOpen ? "bg-sidebar-accent text-sidebar-primary font-medium" : ""}
              >
                <Lightbulb className="h-4 w-4" />
                <span className="text-sm">Idée</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )}

          {/* Administration (admin/super-admin only) */}
          {!collapsed && (isAdmin || isSuperAdmin) && (
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    tooltip="Administration"
                    className={
                      location.pathname.startsWith("/admin") || location.pathname.startsWith("/super-admin")
                        ? "bg-sidebar-accent text-sidebar-primary font-medium"
                        : ""
                    }
                  >
                    <Shield className="h-4 w-4" />
                    <span className="text-sm">Administration</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-48">
                  {isAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/admin")}>
                      <ShieldCheck className="h-4 w-4 mr-2" />
                      Admin
                    </DropdownMenuItem>
                  )}
                  {isSuperAdmin && (
                    <DropdownMenuItem onClick={() => navigate("/super-admin")}>
                      <ShieldAlert className="h-4 w-4 mr-2" />
                      Super Admin
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          )}

          {/* Bottom row: Settings + Logout */}
          <div
            className={
              collapsed
                ? "flex flex-col items-center gap-1 w-full px-2 border-t border-sidebar-border pt-2 mt-1"
                : "flex flex-col gap-1 w-full px-2 border-t border-sidebar-border pt-2 mt-1"
            }
          >
            <SidebarMenuItem className="w-full">
              <div className={collapsed ? "flex flex-col items-center gap-1" : "flex items-center gap-1 w-full"}>
                <SidebarMenuButton
                  tooltip="Réglages"
                  onClick={() => navigate("/settings")}
                  className={collapsed ? "justify-center" : "flex-1 justify-start"}
                >
                  <Settings className="h-4 w-4" />
                  {!collapsed && <span className="text-sm">Réglages</span>}
                </SidebarMenuButton>
                <ThemeToggle />
              </div>
            </SidebarMenuItem>
            <SidebarMenuItem className="w-full">
              <SidebarMenuButton
                tooltip="Déconnexion"
                onClick={() => {
                  signOut();
                  navigate("/");
                }}
                className={collapsed ? "justify-center" : "w-full justify-start"}
              >
                <LogOut className="h-4 w-4" />
                {!collapsed && <span className="text-sm">Déconnexion</span>}
              </SidebarMenuButton>
            </SidebarMenuItem>
          </div>
        </SidebarMenu>
      </SidebarFooter>

      <Suspense fallback={null}>
        {isAdmin && <AdminIdeaQuickInput open={ideaOpen} onOpenChange={setIdeaOpen} />}
      </Suspense>
    </Sidebar>
  );
}
